import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { lcAccountsApi, lcRolesApi } from '@/api/roles'
import type { LcAccount, LcAccountCreateRequest, LcAccountUpdateRequest } from '@/types/rbac'

const ROLE_BADGE: Record<string, string> = {
  owner: '원장',
  admin: '직원',
}

type RoleOption = { id: number; name: string }

function formatDateTime(value?: string | null) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}.${month}.${day} ${hours}:${minutes}`
}

function resolveAccountRole(account: LcAccount) {
  if (account.role === 'owner') return ROLE_BADGE.owner
  return account.lcAdminRoleName ?? '미지정'
}

export default function CenterAccounts() {
  const qc = useQueryClient()
  const [query, setQuery] = useState('')
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<LcAccount | null>(null)
  const [resetTarget, setResetTarget] = useState<LcAccount | null>(null)

  const { data: accounts = [] } = useQuery({
    queryKey: ['lc-accounts'],
    queryFn: async () => (await lcAccountsApi.list()).data.data,
  })
  const { data: roleList } = useQuery({
    queryKey: ['lc-roles'],
    queryFn: async () => (await lcRolesApi.getRoles()).data.data,
  })
  const roles: RoleOption[] = (roleList?.roles ?? []).map((r) => ({ id: r.id, name: r.name }))

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lc-accounts'] })

  const createMutation = useMutation({
    mutationFn: (d: LcAccountCreateRequest) => lcAccountsApi.create(d),
    onSuccess: () => {
      invalidate()
      setIsAddOpen(false)
      toast.success('계정이 생성되었습니다.')
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || '계정 생성 실패'),
  })
  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: LcAccountUpdateRequest }) =>
      lcAccountsApi.update(userId, data),
    onSuccess: () => {
      invalidate()
      setEditTarget(null)
      toast.success('계정 정보를 수정했습니다.')
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || '계정 수정 실패'),
  })
  const resetMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword?: string }) =>
      lcAccountsApi.resetPassword(userId, newPassword),
    onSuccess: (res) => {
      setResetTarget(null)
      toast.success(`비밀번호를 초기화했습니다. 임시 비밀번호: ${res.data.data.temporaryPassword}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || '비밀번호 초기화 실패'),
  })
  const deactivateMutation = useMutation({
    mutationFn: (userId: number) => lcAccountsApi.delete(userId),
    onSuccess: () => { invalidate(); toast.success('계정을 비활성화했습니다.') },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || '비활성화 실패'),
  })

  const q = query.trim().toLowerCase()
  const filtered = useMemo(
    () =>
      !q
        ? accounts
        : accounts.filter((a) =>
            [a.fullName ?? '', a.email, a.phoneNumber ?? '', a.lcAdminRoleName ?? '', ROLE_BADGE[a.role] ?? a.role]
              .some((s) => s.toLowerCase().includes(q))
          ),
    [accounts, q]
  )

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-gray-900">센터 계정 관리</h1>

      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border-b">
          <input
            placeholder="이름·이메일·연락처·역할 검색"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setIsAddOpen(true)}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
          >
            + 계정 추가
          </button>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-3 py-2">역할</th>
              <th className="px-3 py-2">이름</th>
              <th className="px-3 py-2">이메일</th>
              <th className="px-3 py-2">연락처</th>
              <th className="px-3 py-2">생성일시</th>
              <th className="px-3 py-2">상태</th>
              <th className="px-3 py-2 text-right">관리</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const isOwner = a.role === 'owner'
              return (
                <tr key={a.userId} className="border-t">
                  <td className="px-3 py-2">{resolveAccountRole(a)}</td>
                  <td className="px-3 py-2 font-medium">{a.fullName ?? '-'}</td>
                  <td className="px-3 py-2">{a.email}</td>
                  <td className="px-3 py-2">{a.phoneNumber || '-'}</td>
                  <td className="px-3 py-2 text-gray-600">{formatDateTime(a.createdAt)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs ${
                        a.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {a.status === 'active' ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {isOwner ? (
                      <span className="text-gray-400 text-xs">변경 불가</span>
                    ) : (
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setEditTarget(a)}
                          className="text-primary-600 hover:underline text-xs"
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => setResetTarget(a)}
                          className="text-gray-600 hover:underline text-xs"
                        >
                          비밀번호 초기화
                        </button>
                        {a.status === 'active' && (
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(`${a.fullName ?? a.email} 계정을 비활성화하시겠습니까?`)) {
                                deactivateMutation.mutate(a.userId)
                              }
                            }}
                            className="text-red-600 hover:underline text-xs"
                          >
                            비활성
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                  {q ? '검색 결과가 없습니다.' : '등록된 계정이 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isAddOpen && (
        <CreateModal
          roles={roles}
          onClose={() => setIsAddOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <EditModal
          account={editTarget}
          roles={roles}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ userId: editTarget.userId, data })}
          submitting={updateMutation.isPending}
        />
      )}

      {resetTarget && (
        <ResetPasswordModal
          account={resetTarget}
          onClose={() => setResetTarget(null)}
          onSubmit={(newPassword) => resetMutation.mutate({ userId: resetTarget.userId, newPassword })}
          submitting={resetMutation.isPending}
        />
      )}
    </div>
  )
}

function CreateModal({
  roles,
  onClose,
  onSubmit,
  submitting,
}: {
  roles: RoleOption[]
  onClose: () => void
  onSubmit: (d: LcAccountCreateRequest) => void
  submitting: boolean
}) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [initialPassword, setInitialPassword] = useState('')
  const [lcAdminRoleId, setLcAdminRoleId] = useState<string>('')

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !fullName.trim() || !phoneNumber.trim() || !initialPassword.trim() || !lcAdminRoleId) {
      toast.error('이메일·이름·연락처·초기 비밀번호·역할은 필수입니다.')
      return
    }
    onSubmit({
      email: email.trim(),
      fullName: fullName.trim(),
      phoneNumber: phoneNumber.trim(),
      initialPassword,
      lcAdminRoleId: Number(lcAdminRoleId),
    })
  }

  return (
    <ModalShell title="계정 추가" titleId="ca-add-title" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <FieldInput label="이메일" value={email} onChange={setEmail} type="email" />
        <FieldInput label="이름" value={fullName} onChange={setFullName} />
        <FieldInput label="연락처" value={phoneNumber} onChange={setPhoneNumber} />
        <FieldInput
          label="초기 비밀번호"
          value={initialPassword}
          onChange={setInitialPassword}
          type="password"
        />
        <RoleSelect roles={roles} value={lcAdminRoleId} onChange={setLcAdminRoleId} />
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="생성" submittingLabel="생성 중..." />
      </form>
    </ModalShell>
  )
}

function EditModal({
  account,
  roles,
  onClose,
  onSubmit,
  submitting,
}: {
  account: LcAccount
  roles: RoleOption[]
  onClose: () => void
  onSubmit: (d: LcAccountUpdateRequest) => void
  submitting: boolean
}) {
  const [email, setEmail] = useState(account.email)
  const [fullName, setFullName] = useState(account.fullName ?? '')
  const [phoneNumber, setPhoneNumber] = useState(account.phoneNumber ?? '')
  const [lcAdminRoleId, setLcAdminRoleId] = useState<string>(
    account.lcAdminRoleId != null ? String(account.lcAdminRoleId) : ''
  )

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !fullName.trim() || !phoneNumber.trim() || !lcAdminRoleId) {
      toast.error('이메일·이름·연락처·역할은 필수입니다.')
      return
    }
    onSubmit({
      email: email.trim(),
      fullName: fullName.trim(),
      // 빈 문자열을 그대로 전송해야 백엔드가 연락처를 비울 수 있다 (undefined면 변경 없이 스킵됨).
      phoneNumber: phoneNumber.trim(),
      lcAdminRoleId: Number(lcAdminRoleId),
      ...(account.status === 'inactive' ? { status: 'active' } : {}),
    })
  }

  return (
    <ModalShell title="계정 수정" titleId="ca-edit-title" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <FieldInput label="이메일 (로그인 ID)" value={email} onChange={setEmail} type="email" />
        <FieldInput label="이름" value={fullName} onChange={setFullName} />
        <FieldInput label="연락처" value={phoneNumber} onChange={setPhoneNumber} />
        <RoleSelect roles={roles} value={lcAdminRoleId} onChange={setLcAdminRoleId} />
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="저장" submittingLabel="저장 중..." />
      </form>
    </ModalShell>
  )
}

function ResetPasswordModal({
  account,
  onClose,
  onSubmit,
  submitting,
}: {
  account: LcAccount
  onClose: () => void
  onSubmit: (newPassword: string | undefined) => void
  submitting: boolean
}) {
  const [password, setPassword] = useState('')

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(password.trim() || undefined)
  }

  return (
    <ModalShell title="비밀번호 초기화" titleId="ca-reset-title" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-gray-600">
          <strong>{account.fullName ?? account.email}</strong> 계정의 비밀번호를 초기화합니다.
        </p>
        <FieldInput label="새 비밀번호 (선택)" value={password} onChange={setPassword} />
        <p className="text-xs text-gray-500">
          비워두면 안전한 임시 비밀번호가 자동 생성됩니다. 직접 입력 시 8자 이상이어야 합니다.
        </p>
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-xs text-amber-700">
            초기화 후 생성된 비밀번호가 화면에 표시됩니다. 해당 사용자에게 안내를 잊지 마세요.
          </span>
        </div>
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="초기화" submittingLabel="초기화 중..." />
      </form>
    </ModalShell>
  )
}

function ModalShell({
  title,
  titleId,
  onClose,
  children,
}: {
  title: string
  titleId: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-lg font-bold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  )
}

function ModalActions({
  onClose,
  submitting,
  submitLabel,
  submittingLabel,
}: {
  onClose: () => void
  submitting: boolean
  submitLabel: string
  submittingLabel: string
}) {
  return (
    <div className="flex justify-end gap-2 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
      >
        취소
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
      >
        {submitting ? submittingLabel : submitLabel}
      </button>
    </div>
  )
}

function RoleSelect({
  roles,
  value,
  onChange,
}: {
  roles: RoleOption[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 mb-1 block">역할</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      >
        <option value="" disabled>역할 선택</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>
    </label>
  )
}

function FieldInput({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 mb-1 block">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      />
    </label>
  )
}
